import * as Sentry from "@sentry/react";
import { scrubDeep } from "@carwash/popia-scrub";
import { ApiError } from "./apiError";
import { appVersion } from "./version";

let enabled = false;

// An invite token in a URL is a live credential (it is stored plaintext server-side, so a
// leaked one grants business access until it expires). scrubDeep handles phone numbers; this
// handles the one secret that legitimately appears in a URL in this app.
// JSON.stringify only returns undefined for undefined/function/symbol inputs, which the `?? null`
// rules out, so the round-trip needs no further guarding.
const scrubInviteTokens = <T>(value: T): T =>
  JSON.parse(
    JSON.stringify(value ?? null).replace(/\/invite\/[A-Za-z0-9]{8,}/g, "/invite/[redacted]"),
  ) as T;

// One bad queued command replays until it succeeds or is dropped; report it once
// rather than once per attempt (spec §2, outbox replay).
const reportedCommands = new Set<string>();

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // blank DSN = fully disabled (dev default)
  enabled = true;
  Sentry.init({
    dsn,
    release: appVersion.version, // the baked APP_VERSION
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    enableLogs: true,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
    // Belt-and-braces: nothing phone-shaped leaves the app, whatever path it took.
    // The walk-in form takes a customer's number, so this matters more here than
    // it does in the customer app.
    beforeSend: (event) => scrubInviteTokens(scrubDeep(event)) as typeof event,
    beforeBreadcrumb: (crumb) => scrubInviteTokens(scrubDeep(crumb)) as typeof crumb,
  });
}

// Business/site are no longer known at startup — they are resolved per session from
// GET /auth/staff/me — so the tags are set once the scope is (ScopeGate), and cleared on
// sign-out. Ids only: no email, no name, nothing that identifies a person (POPIA).
export function setScopeTags(scope: { businessId: string; siteId: string } | null): void {
  if (!enabled) return;
  Sentry.setTags({ businessId: scope?.businessId ?? "", siteId: scope?.siteId ?? "" });
}

export function captureApiFailure(
  err: unknown,
  context: { path: string; clientCommandId?: string },
): void {
  // Deliberate gate: customer-web's captureApiFailure/logEvent don't check `enabled`
  // (only setSentryUser does there) because an uninitialised SDK simply no-ops. This
  // file gates explicitly instead — don't "harmonise" the two away; they're allowed
  // to differ, this just makes the no-DSN-configured path an early return here too.
  if (!enabled) return;
  // Expected 4xxs are UX states, not engineering signal.
  if (err instanceof ApiError && err.status < 500) return;
  // A transport failure while offline is the offline design working, not a bug —
  // under load shedding this would otherwise drown every real error.
  if (!(err instanceof ApiError) && !navigator.onLine) return;
  if (context.clientCommandId) {
    if (reportedCommands.has(context.clientCommandId)) return;
    reportedCommands.add(context.clientCommandId);
  } else {
    // Queries (e.g. listActiveVisits) carry no clientCommandId, so the dedupe above
    // never applies to them. A polling query (30s refetchInterval, retry: false)
    // failing against a 5xx-ing backend would otherwise emit one captureException
    // per poll — ~120/hour/device, times every tablet on every site. Dedupe once per
    // path+failure-shape per page load instead; a genuinely different failure (a
    // different route, or a transport error vs a specific HTTP status) still reports.
    const key = `${context.path}:${err instanceof ApiError ? err.status : "transport"}`;
    if (reportedCommands.has(key)) return;
    reportedCommands.add(key);
  }
  Sentry.captureException(err, { tags: { endpoint: context.path } });
}

export function logEvent(name: string, data?: Record<string, string | number | boolean>): void {
  if (!enabled) return;
  const scrubbed = data ? (scrubDeep(data) as Record<string, string | number | boolean>) : undefined;
  // Breadcrumbs attach to the next error even if Sentry Logs are empty/disabled.
  Sentry.addBreadcrumb({ category: "app", message: name, data: scrubbed, level: "info" });
  // Sentry Logs search in this org indexes string attributes (closeReason showed
  // up; emptyFrames/videoWidth as integers did not). Keep breadcrumbs typed.
  const attrs = scrubbed
    ? Object.fromEntries(Object.entries(scrubbed).map(([k, v]) => [k, typeof v === "string" ? v : String(v)]))
    : undefined;
  Sentry.logger.info(name, attrs);
}
